// Frontend client for the secure serverless API. The private key lives only on
// the server; the browser never sees it. Trades are authorized with a password
// the user types (never stored in the bundle).

const TOKEN_ADDRESS = '0x9aeb2e6dd8d55e14292acfcfc4077e33106e4144'; // PAYU

// Admin login: validates username + password server-side (the password is never
// hardcoded in this bundle). On success the caller stores the password to
// authorize subsequent trades. Returns true, or throws with the server message.
export const apiLogin = async ({ username, password }) => {
  const res = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `Login failed (${res.status})`);
  return true;
};

// Read endpoints are password-protected too. The password saved at login lives
// under these keys (same as LoginGate's PW_KEY / AUTH_FLAG; literals here to
// avoid a circular import with LoginGate, which imports apiLogin).
const PW_KEY = 'payubot_pw';
const AUTH_FLAG = 'payubot_authed';
const storedPassword = () => { try { return JSON.parse(localStorage.getItem(PW_KEY)) || ''; } catch (e) { return ''; } };

// GET with the saved password. A 401/429 means the saved password is wrong
// (e.g. TRADE_PASSWORD was changed) or locked out: drop the session and go back
// to the login screen, instead of polling with a bad password and tripping the
// server's failed-attempt lock.
const authedGet = async (url, label) => {
  const res = await fetch(url, { cache: 'no-store', headers: { 'x-trade-password': storedPassword() } });
  if (res.status === 401 || res.status === 429) {
    try { localStorage.removeItem(AUTH_FLAG); localStorage.removeItem(PW_KEY); } catch (e) {}
    window.location.href = '/';
    throw new Error('Session expired — please log in again');
  }
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `${label} failed (${res.status})`);
  return data;
};

// Wallet status: address, balances, prices, network.
export const apiGetStatus = (token = TOKEN_ADDRESS) => authedGet(`/api/status?token=${token}`, 'Status');

// Recent trades + per-token cron run status + running cost counters.
// -> { ok, configured, trades, cronStatus: { SYM: {...} }, costs: { SYM: { day, month, all, since } } }
export const apiGetHistory = (limit = 50) => authedGet(`/api/history?limit=${limit}`, 'History');

// Balances across all configured wallets, valued in USD, richest-first.
// -> { ok, count, bnbUsd, priceUsd, supply, totals, wallets, failedReads, storeDegraded }
export const apiGetWallets = () => authedGet('/api/wallets', 'Wallets');

// Add a wallet address to the Maradona panel (stored server-side in Redis).
// Password-protected. Returns { ok, added, duplicate }.
export const apiAddWallet = async ({ address, password }) => {
  const res = await fetch('/api/wallet-add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-trade-password': password || '' },
    body: JSON.stringify({ address })
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `Add failed (${res.status})`);
  return data;
};

// Remove (hide) a wallet from the Maradona panel. Password-protected. Reversible.
export const apiRemoveWallet = async ({ address, password }) => {
  const res = await fetch('/api/wallet-remove', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-trade-password': password || '' },
    body: JSON.stringify({ address })
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `Remove failed (${res.status})`);
  return data;
};

// Execute a trade on-chain (signed server-side). action: 'buy' | 'sell' | 'cycle'.
export const apiTrade = async ({ action, amount, password, slippage, gasSettings, token = TOKEN_ADDRESS }) => {
  const res = await fetch('/api/trade', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-trade-password': password || '' },
    body: JSON.stringify({ action, amount, slippage, gasSettings, token })
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `Trade failed (${res.status})`);
  return data; // { ok: true, trades: [...] }
};
